import { catalogEntry, parseContract } from "@helm/core";
import { marketPhase } from "@helm/core/bars";
import { formatPercent, formatTicks, formatVolume } from "@helm/ui";
import { priceFormatOf, useMarket, useMdStatus } from "../md/marketData";
import { quoteRow, rangeSinceSubscribed } from "./useMarkets";

/** Label, value, optional tooltip. */
type Line = [string, React.ReactNode, (string | undefined)?];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function contractLabel(contract: string | null): string | null {
  if (!contract) return null;
  const c = parseContract(contract);
  if (!c) return null;
  const month = MONTHS["FGHJKMNQUVXZ".indexOf(c.month)] ?? c.month;
  const year = c.year.length === 1 ? `202${c.year}` : `20${c.year}`;
  return `${month} ${year}`;
}

function money(usd: number): string {
  return `$${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
}

/**
 * Details tab (TradingView "Details", futures subset; owner 2026-09-28): the selected symbol's
 * contract, exchange, tick size / value, session, last, change, day range and volume. The live
 * `InstrumentInfo` wins for contract, exchange and ticks; the catalog fills in the name and
 * session. Change / range / volume are since the first print this window received until the
 * gateway streams `SessionStats` (docs/PLAN.md TODO), and are labelled so.
 */
export function DetailsView({ symbol }: { symbol: string | null }): React.JSX.Element {
  const m = useMarket(symbol);
  const status = useMdStatus();
  if (symbol === null) {
    return (
      <div className="empty-state">
        <div className="empty-title">No symbol</div>
        <div className="muted">Pick a symbol in the watchlist, or click a chart.</div>
      </div>
    );
  }
  const entry = catalogEntry(m?.info?.symbol ?? symbol);
  const info = m?.info ?? null;
  const f = priceFormatOf(info);
  const r = quoteRow(symbol);
  const range = rangeSinceSubscribed(symbol);
  const tickNum = info?.tickSizeNum ?? entry?.tickSizeNum ?? null;
  const tickDen = info?.tickSizeDen ?? entry?.tickSizeDen ?? null;
  const tickSize = tickNum !== null && tickDen ? tickNum / tickDen : null;
  const tickValue =
    info && info.tickValueCents > 0 ? info.tickValueCents / 100 : (entry?.tickValueUsd ?? null);
  const tone =
    r.changeTicks === null ? "none" : r.changeTicks > 0 ? "pos" : r.changeTicks < 0 ? "neg" : "zero";
  const phase = marketPhase(Date.now());
  const contract = info?.symbol ?? (parseContract(symbol) ? symbol : null);
  const rows: Line[] = [
    [
      "Contract",
      contract
        ? `${contract}${contractLabel(contract) ? ` · ${contractLabel(contract) ?? ""}` : ""}`
        : "resolving…",
      symbol !== contract && contract ? `Front month of ${symbol}, resolved by the gateway` : undefined,
    ],
    ["Exchange", info?.exchange ?? entry?.exchange ?? "—"],
    [
      "Tick size",
      tickSize === null
        ? "—"
        : `${String(tickSize)}${tickNum !== null && tickDen ? ` (${String(tickNum)}/${String(tickDen)})` : ""}`,
    ],
    ["Tick value", tickValue === null ? "—" : `${money(tickValue)} per contract`],
    ["Session", entry?.session ?? "—"],
    [
      "Now",
      phase === "rth" ? "RTH open" : phase === "eth" ? "Overnight (ETH)" : "Closed",
      "CME equity-index session phase (holidays not modelled)",
    ],
  ];
  const market: Line[] = [
    ["Last", formatTicks(r.lastTicks, f)],
    [
      "Change",
      <span key="c" className={`tone-${tone}`}>
        {r.changeTicks === null
          ? "—"
          : `${r.changeTicks > 0 ? "+" : r.changeTicks < 0 ? "−" : ""}${formatTicks(Math.abs(r.changeTicks), f)}${r.changePct === null ? "" : ` (${formatPercent(r.changePct)})`}`}
      </span>,
      "Since the first print this window received (session stats not streamed yet)",
    ],
    [
      "Range",
      range ? `${formatTicks(range.low, f)} – ${formatTicks(range.high, f)}` : "—",
      "Low – high since this window subscribed (session stats not streamed yet)",
    ],
    ["Volume", r.trades > 0 ? formatVolume(r.volume) : "—", "Contracts since this window subscribed"],
    [
      "Bid × Ask",
      m?.bidTicks != null && m.askTicks != null
        ? `${formatTicks(m.bidTicks, f)} × ${formatTicks(m.askTicks, f)}`
        : "—",
    ],
  ];
  return (
    <div className="details">
      <div className="details-head">
        <span className="details-sym">{symbol}</span>
        <span className="details-name muted">{entry?.name ?? ""}</span>
        <span className="details-last num">{formatTicks(r.lastTicks, f)}</span>
        <span className={`details-chg num tone-${tone}`}>
          {r.changePct === null ? "" : formatPercent(r.changePct)}
        </span>
      </div>
      <dl className="details-grid">
        {rows.map(([k, v, t]) => (
          <div key={k} className="details-row" title={t}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      <div className="details-sub muted">Market (since subscribed)</div>
      <dl className="details-grid">
        {market.map(([k, v, t]) => (
          <div key={k} className="details-row" title={t}>
            <dt>{k}</dt>
            <dd className="num">{v}</dd>
          </div>
        ))}
      </dl>
      {status !== "connected" && (
        <div className="muted details-note">
          {status === "off" ? "Prices stream once signed in." : "Market data is not connected."}
        </div>
      )}
      {m?.notice && <div className="muted details-note">{m.notice}</div>}
    </div>
  );
}
